import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Trash2, ShoppingBag, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, API } from "@/lib/api";

export const CartDrawer = () => {
  const { items, updateQty, removeItem, subtotal, discount, total, promo, setPromo, clearPromo, open, setOpen, lineKey } = useCart();
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [method, setMethod] = useState("paypal");

  const applyPromo = async () => {
    if (!code.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/promo/validate", { code: code.trim() });
      setPromo(data);
      setCode("");
      toast.success(`Promo "${data.code}" applied!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid promo code");
    } finally { setApplying(false); }
  };

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size || "", color: i.color || "" })),
        origin_url: window.location.origin,
        promo_code: promo?.code || "",
        provider: method,
      };
      const { data } = await api.post("/store/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] bg-black/60"
          onClick={() => setOpen(false)}
          data-testid="cart-drawer"
        >
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#0A1128] text-white p-5 flex items-center justify-between">
              <h3 className="font-display text-xl uppercase inline-flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Your Cart</h3>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="w-6 h-6" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {items.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  Your cart is empty.
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((i) => {
                    const key = lineKey(i);
                    const variant = [i.size, i.color].filter(Boolean).join(" · ");
                    return (
                      <div key={key} data-testid="cart-item" className="flex gap-3 border-b border-slate-100 pb-4">
                        <div className="w-16 h-16 bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                          {i.image_file_id ? <img src={`${API}/public/files/${i.image_file_id}`} alt={i.name} className="w-full h-full object-cover" /> : <ShoppingBag className="w-6 h-6 text-slate-300" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-[#0A1128] leading-tight">{i.name}</p>
                          {variant && <p className="text-xs text-slate-500">{variant}</p>}
                          <p className="text-sm text-[#D90429] font-bold mt-0.5">${i.price.toFixed(2)}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex items-center border border-slate-300">
                              <button data-testid={`cart-dec-${key}`} onClick={() => updateQty(key, i.quantity - 1)} className="px-2 py-1 hover:bg-slate-100"><Minus className="w-3.5 h-3.5" /></button>
                              <span className="px-3 text-sm font-semibold">{i.quantity}</span>
                              <button data-testid={`cart-inc-${key}`} onClick={() => updateQty(key, i.quantity + 1)} className="px-2 py-1 hover:bg-slate-100"><Plus className="w-3.5 h-3.5" /></button>
                            </div>
                            <button data-testid={`cart-remove-${key}`} onClick={() => removeItem(key)} className="text-slate-300 hover:text-[#D90429]"><Trash2 className="w-4 h-4" /></button>
                          </div>
                        </div>
                        <div className="text-sm font-bold text-[#0A1128] shrink-0">${(i.price * i.quantity).toFixed(2)}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-slate-200 p-5 space-y-4">
                {/* Promo */}
                {promo ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 px-3 py-2" data-testid="promo-applied">
                    <span className="text-sm text-green-700 font-semibold">Code {promo.code} applied</span>
                    <button data-testid="promo-remove" onClick={clearPromo} className="text-green-700 hover:text-[#D90429]"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="promo-input"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && applyPromo()}
                      placeholder="Promo code"
                      className="flex-1 border border-slate-300 px-3 py-2 text-sm uppercase focus:outline-none focus:border-[#D90429]"
                    />
                    <button data-testid="promo-apply" onClick={applyPromo} disabled={applying || !code.trim()} className="bg-[#0A1128] text-white px-4 py-2 uppercase tracking-[0.1em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-50">
                      {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                )}

                <div className="space-y-1">
                  <div className="flex justify-between text-sm text-slate-500">
                    <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-green-600 font-semibold" data-testid="cart-discount">
                      <span>Discount</span><span>-${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-1">
                    <span className="text-sm uppercase tracking-[0.15em] text-slate-500 font-semibold">Total</span>
                    <span data-testid="cart-subtotal" className="font-display text-2xl text-[#0A1128]">${total.toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400">Shipping & taxes calculated at checkout.</p>
                <div className="grid grid-cols-2 gap-2" data-testid="cart-payment-method">
                  {[["paypal", "PayPal"], ["stripe", "Card"]].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`cart-pay-${val}`}
                      onClick={() => setMethod(val)}
                      className={`py-2.5 uppercase tracking-[0.1em] text-xs font-bold border-2 transition-colors duration-200 ${
                        method === val
                          ? "border-[#0A1128] bg-[#0A1128] text-white"
                          : "border-slate-200 text-slate-500 hover:border-[#0A1128]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button data-testid="cart-checkout" onClick={checkout} disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4" /> Checkout with {method === "paypal" ? "PayPal" : "Card"}</>}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
